import type { BuyerUI } from '@choco-admin/types';
import { TableRow, TableCell, Button } from '@heroui/react';
import React from 'react';
import { FiEdit, FiEye, FiUsers } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import {
  LoadingTable,
  LoadingTableColumn,
} from '../../../../../components/Table';

interface BuyerTableProps {
  buyers: BuyerUI[];
  isLoading?: boolean;
}

export const BuyerTable: React.FC<BuyerTableProps> = ({
  buyers,
  isLoading = false,
}) => {
  const navigate = useNavigate();

  const handleViewBuyer = (buyerId: string) => {
    navigate(`/buyers/${buyerId}`);
  };

  const handleEditBuyer = (buyerId: string) => {
    navigate(`/buyers/${buyerId}`);
  };

  const columns: LoadingTableColumn[] = [
    { label: 'Buyer', className: 'pl-6' },
    { label: 'Reference', className: 'hidden md:table-cell' },
    { label: 'Location', className: 'hidden lg:table-cell' },
    { label: 'Actions', width: 80, className: 'pr-6' },
  ];

  const renderRow = (buyer: BuyerUI) => (
    <TableRow key={buyer.id}>
      <TableCell className="pl-6">
        <div className="flex flex-col">
          <span className="font-medium text-foreground">{buyer.name}</span>
          {buyer.displayName && buyer.displayName !== buyer.name && (
            <span className="text-sm text-default-500">
              {buyer.displayName}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <span className="text-sm text-foreground">
          {buyer.referenceId || '-'}
        </span>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <span className="text-sm text-foreground">
          {buyer.city
            ? `${buyer.city}${buyer.country ? `, ${buyer.country}` : ''}`
            : '-'}
        </span>
      </TableCell>
      <TableCell className="pr-6">
        <div className="flex gap-2">
          <Button
            isIconOnly
            size="sm"
            variant="flat"
            color="primary"
            onPress={() => handleViewBuyer(buyer.id)}
            aria-label="View buyer"
          >
            <FiEye className="h-4 w-4" />
          </Button>
          <Button
            isIconOnly
            size="sm"
            variant="flat"
            color="secondary"
            onPress={() => handleEditBuyer(buyer.id)}
            aria-label="Edit buyer"
          >
            <FiEdit className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <LoadingTable
      columns={columns}
      data={buyers}
      renderRow={renderRow}
      isLoading={isLoading}
      isEmpty={buyers.length === 0}
      rowCount={5}
      ariaLabel="Buyers table"
      emptyState={{
        title: 'No buyers found',
        description: 'There are no buyers available.',
        icon: FiUsers,
      }}
    />
  );
};
